import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, X, Image as ImageIcon, Hotel, Eye, CalendarDays, CheckCircle2, Clock3, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { queryDocuments, createDocument, updateDocument, deleteDocument } from '../../services/firestoreService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, onSnapshot } from 'firebase/firestore';
import { db, storage } from '../../services/firebase';
import { summarizeServiceDay, toDate, toDateKey } from '../../utils/reservationAnalytics';
import LoadingState from '../shared/LoadingState';
import './Services.css';

const Services = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const serviceTypes = [
    { value: 'boat', label: 'Lancha' },
    { value: 'schooner', label: 'Escuna' },
    { value: 'inn', label: 'Pousada' },
    { value: 'snack_bar', label: 'Lanchonete' },
    { value: 'bar', label: 'Bar' },
    { value: 'restaurant', label: 'Restaurante' },
    { value: 'tour', label: 'Passeio' },
    { value: 'transfer', label: 'Transfer' },
    { value: 'experience', label: 'Experiência' }
  ];

  const getServiceTypeLabel = (service) => {
    if (service.isPousada || service.type === 'inn' || service.type === 'pousada') return 'Pousada';
    return serviceTypes.find(type => type.value === service.type)?.label || service.type || 'Serviço';
  };

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [operationalService, setOperationalService] = useState(null);
  const [operationalDate, setOperationalDate] = useState(toDateKey(new Date()));
  const [serviceReservations, setServiceReservations] = useState([]);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'boat',
    capacity: '',
    price: '',
    commission: '',
    imageUrl: '',
    description: '',
    // Pousada fields
    isPousada: false,
    rooms: []
  });

  useEffect(() => {
    loadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  useEffect(() => {
    if (!operationalService) return undefined;
    const companyId = operationalService.companyId || getCompanyId();
    if (!companyId) return undefined;

    setLoadingOperations(true);
    const reservationsRef = collection(db, `companies/${companyId}/reservations`);
    return onSnapshot(reservationsRef, (snapshot) => {
      setServiceReservations(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
      setLoadingOperations(false);
    }, (error) => {
      console.error('Erro ao acompanhar check-ins do serviço:', error);
      setLoadingOperations(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationalService]);

  const loadServices = async () => {
    if (!userData) return;

    try {
      setLoading(true);

      // If collaborator with multiple companies, aggregate services from all companies
      if (userData.role === 'collaborator' && Array.isArray(userData.companies) && userData.companies.length > 0) {
        const fetches = userData.companies.map(c =>
          queryDocuments(
            `companies/${c.companyId}/services`,
            [],
            { field: 'createdAt', direction: 'desc' }
          ).then(res => ({ company: c, data: res.data || [] }))
        );

        const results = await Promise.all(fetches);
        // Flatten and attach company meta
        const aggregated = results.flatMap(r => r.data.map(s => ({ ...s, companyId: r.company.companyId, companyName: r.company.companyName })));
        // Sort by createdAt desc if available
        aggregated.sort((a, b) => {
          const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
          const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
          return tb - ta;
        });

        setServices(aggregated);
      } else if (userData?.companyId || userData?.currentCompanyId) {
        const cid = userData?.companyId || userData?.currentCompanyId;
        const { data } = await queryDocuments(
          `companies/${cid}/services`,
          [],
          { field: 'createdAt', direction: 'desc' }
        );
        setServices(data);
      } else {
        setServices([]);
      }
    } catch (error) {
      console.error('Erro ao carregar serviços:', error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper para operações de escrita: colaborador atua na `currentCompanyId`
  const getCompanyId = () => {
    return userData?.role === 'collaborator' ? userData?.currentCompanyId : userData?.companyId;
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Imagem muito grande. Máximo 5MB.');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.imageUrl || '';

    setUploading(true);
    try {
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `services/${getCompanyId()}/${Date.now()}.${fileExt}`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, imageFile);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      alert('Firebase Storage não está configurado ou houve um erro. O serviço será salvo sem imagem.');
      return formData.imageUrl || '';
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let imageUrl = formData.imageUrl || '';
    
    // Tentar fazer upload da imagem se houver
    if (imageFile) {
      imageUrl = await uploadImage();
    }
    
    const serviceData = {
      name: formData.name,
      type: formData.type,
      capacity: formData.isPousada ? 0 : parseInt(formData.capacity || 0),
      price: parseFloat(formData.price),
      commission: parseFloat(formData.commission),
      companyId: getCompanyId(),
      imageUrl: imageUrl,
      description: formData.description || '',
      isPousada: formData.type === 'inn' || formData.isPousada || false,
      rooms: (formData.type === 'inn' || formData.isPousada) ? formData.rooms : []
    };

    try {
      if (editingService) {
        await updateDocument(
          `companies/${getCompanyId()}/services`,
          editingService.id,
          serviceData
        );
      } else {
        await createDocument(
          `companies/${getCompanyId()}/services`,
          serviceData
        );
      }

      setShowModal(false);
      setEditingService(null);
      setFormData({ 
        name: '', 
        type: 'boat', 
        capacity: '', 
        price: '', 
        commission: '', 
        imageUrl: '', 
        description: '',
        isPousada: false,
        rooms: []
      });
      setImageFile(null);
      setImagePreview('');
      loadServices();
    } catch (error) {
      console.error('Erro ao salvar serviço:', error);
      alert('Erro ao salvar serviço. Verifique os dados e tente novamente.');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      type: service.type,
      capacity: service.capacity?.toString() || '',
      price: service.price?.toString() || '',
      commission: service.commission?.toString() || '',
      imageUrl: service.imageUrl || '',
      description: service.description || '',
      isPousada: service.isPousada || service.type === 'inn' || service.type === 'pousada' || false,
      rooms: service.rooms || []
    });
    setImagePreview(service.imageUrl || '');
    setShowModal(true);
  };

  const addRoom = () => {
    setFormData({
      ...formData,
      rooms: [
        ...formData.rooms,
        { name: '', capacity: '', price: '', description: '' }
      ]
    });
  };

  const updateRoom = (index, field, value) => {
    const updatedRooms = [...formData.rooms];
    updatedRooms[index][field] = value;
    setFormData({ ...formData, rooms: updatedRooms });
  };

  const removeRoom = (index) => {
    setFormData({
      ...formData,
      rooms: formData.rooms.filter((_, i) => i !== index)
    });
  };

  const handleDelete = async (serviceId) => {
    if (window.confirm(t('common.confirm'))) {
      await deleteDocument(`companies/${getCompanyId()}/services`, serviceId);
      loadServices();
    }
  };

  const openOperationalView = (service) => {
    setOperationalDate(toDateKey(new Date()));
    setServiceReservations([]);
    setOperationalService(service);
  };

  const operationalSummary = operationalService
    ? summarizeServiceDay(operationalService, serviceReservations, operationalDate)
    : null;

  const formatReservationTime = (value) => {
    const date = toDate(value);
    return date ? date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '--:--';
  };

  if (loading) return <LoadingState title="Carregando servicos" subtitle="Atualizando catalogo da empresa" />;

  return (
    <div className="services-container">
      <div className="page-header">
        <h1>{t('services.title')}</h1>
        <button onClick={() => setShowModal(true)} className="btn-primary">
          <Plus size={20} />
          {t('services.new')}
        </button>
      </div>

      <div className="services-grid">
        {services.map((service) => (
          <div key={service.id} className="service-card">
            {service.imageUrl && (
              <div className="service-image">
                <img src={service.imageUrl} alt={service.name} />
              </div>
            )}
            <div className="service-header">
              <h3>{service.name}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {service.companyName && (
                  <small style={{ color: '#64748b' }}>{service.companyName}</small>
                )}
                <span className="service-type">
                  {service.isPousada || service.type === 'inn' || service.type === 'pousada' ? (
                    <>
                      <Hotel size={14} style={{ marginRight: '4px' }} />
                      Pousada
                    </>
                  ) : (
                    getServiceTypeLabel(service)
                  )}
                </span>
              </div>
            </div>
            <div className="service-details">
              {service.description && (
                <p className="service-description">{service.description}</p>
              )}
              {service.isPousada || service.type === 'inn' || service.type === 'pousada' ? (
                <>
                  <p><strong>Quartos:</strong> {service.rooms?.length || 0}</p>
                  <p><strong>Capacidade Total:</strong> {service.rooms?.reduce((sum, r) => sum + parseInt(r.capacity || 0), 0) || 0} pessoas</p>
                  <p><strong>A partir de:</strong> R$ {Math.min(...(service.rooms?.map(r => parseFloat(r.price || 0)) || [0])).toFixed(2)}</p>
                </>
              ) : (
                <>
                  <p><strong>{t('services.capacity')}:</strong> {service.capacity}</p>
                  <p><strong>{t('services.price')}:</strong> R$ {(service.price || 0).toFixed(2)}</p>
                </>
              )}
              <p><strong>{t('services.commission')}:</strong> {(service.commission || 0)}%</p>
            </div>
            <div className="service-actions">
              <button onClick={() => openOperationalView(service)} className="btn-service-view">
                <Eye size={18} />
                Ver operação
              </button>
              <button onClick={() => handleEdit(service)} className="btn-icon" title="Editar serviço" aria-label="Editar serviço">
                <Edit size={18} />
              </button>
              <button onClick={() => handleDelete(service.id)} className="btn-icon delete" title="Excluir serviço" aria-label="Excluir serviço">
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {operationalService && operationalSummary && (
        <div className="modal-overlay" onClick={() => setOperationalService(null)}>
          <div className="service-operation-modal" onClick={(event) => event.stopPropagation()}>
            <div className="service-operation-header">
              <div>
                <span className="operation-eyebrow">Operação do serviço</span>
                <h2>{operationalService.name}</h2>
              </div>
              <button className="btn-icon" type="button" onClick={() => setOperationalService(null)} title="Fechar" aria-label="Fechar">
                <X size={20} />
              </button>
            </div>

            <label className="operation-date-field">
              <CalendarDays size={18} />
              <span>Data consultada</span>
              <input type="date" value={operationalDate} onChange={(event) => setOperationalDate(event.target.value)} />
            </label>

            <div className="operation-metrics">
              <div className="operation-metric operation-metric--expected">
                <Users size={20} />
                <span>Esperados</span>
                <strong>{operationalSummary.expectedPeople}</strong>
              </div>
              <div className="operation-metric operation-metric--checked">
                <CheckCircle2 size={20} />
                <span>Check-ins feitos</span>
                <strong>{operationalSummary.checkedPeople}</strong>
              </div>
              <div className="operation-metric operation-metric--missing">
                <Clock3 size={20} />
                <span>Ainda faltam</span>
                <strong>{operationalSummary.missingPeople}</strong>
              </div>
              <div className="operation-metric">
                <CalendarDays size={20} />
                <span>Reservas ativas</span>
                <strong>{operationalSummary.reservationCount}</strong>
              </div>
            </div>

            <div className="operation-capacity-row">
              <span>Taxa de check-in <strong>{operationalSummary.checkInRate.toFixed(0)}%</strong></span>
              {operationalSummary.availablePeople !== null && (
                <span>Vagas livres <strong>{operationalSummary.availablePeople}</strong> de {operationalSummary.capacity}</span>
              )}
              {operationalSummary.cancelledCount > 0 && <span>Canceladas <strong>{operationalSummary.cancelledCount}</strong></span>}
            </div>

            <div className="operation-reservations">
              <h3>Clientes do dia</h3>
              {loadingOperations ? (
                <LoadingState variant="block" title="Atualizando check-ins" subtitle="Sincronizando a operação" bars={2} />
              ) : operationalSummary.validReservations.length === 0 ? (
                <p className="operation-empty">Nenhuma reserva ativa para esta data.</p>
              ) : (
                <div className="operation-table-wrap">
                  <table className="operation-table">
                    <thead><tr><th>Horário</th><th>Cliente</th><th>Pessoas</th><th>Check-in</th></tr></thead>
                    <tbody>
                      {operationalSummary.validReservations.map((reservation) => (
                        <tr key={reservation.id}>
                          <td data-label="Horário">{formatReservationTime(reservation.date)}</td>
                          <td data-label="Cliente"><strong>{reservation.clientName || 'Cliente'}</strong>{reservation.roomName && <small>{reservation.roomName}</small>}</td>
                          <td data-label="Pessoas">{reservation.participants || reservation.guests || 1}</td>
                          <td data-label="Check-in"><span className={`operation-status ${reservation.checkInStatus === 'checked_in' ? 'is-checked' : 'is-pending'}`}>{reservation.checkInStatus === 'checked_in' ? 'Realizado' : 'Aguardando'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content service-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingService ? t('services.edit') : t('services.new')}</h2>
            <form onSubmit={handleSubmit}>
              
              {/* Image Upload */}
              <div className="form-group">
                <label>Imagem do Serviço</label>
                <div className="image-upload-container">
                  {imagePreview ? (
                    <div className="image-preview">
                      <img src={imagePreview} alt="Preview" />
                      <button 
                        type="button" 
                        className="btn-remove-image"
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview('');
                          setFormData({ ...formData, imageUrl: '' });
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="image-upload-label">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        style={{ display: 'none' }}
                      />
                      <ImageIcon size={32} />
                      <span>Clique para adicionar imagem</span>
                      <small>Máximo 5MB</small>
                    </label>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>{t('services.name')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  placeholder="Descrição opcional do serviço"
                />
              </div>

              <div className="form-group">
                <label>{t('services.type')}</label>
                <select
                  value={formData.type === 'pousada' ? 'inn' : formData.type}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setFormData({
                      ...formData,
                      type: nextType,
                      isPousada: nextType === 'inn'
                    });
                  }}
                >
                  {serviceTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {!(formData.type === 'inn' || formData.isPousada) ? (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('services.capacity')}</label>
                      <input
                        type="number"
                        value={formData.capacity}
                        onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('services.price')}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="rooms-section">
                  <div className="rooms-header">
                    <h3>Quartos</h3>
                    <button type="button" onClick={addRoom} className="btn-add-room">
                      <Plus size={16} />
                      Adicionar Quarto
                    </button>
                  </div>

                  {formData.rooms.length === 0 && (
                    <p className="no-rooms">Nenhum quarto cadastrado. Clique em "Adicionar Quarto" para começar.</p>
                  )}

                  {formData.rooms.map((room, index) => (
                    <div key={index} className="room-item">
                      <div className="room-header">
                        <h4>Quarto {index + 1}</h4>
                        <button 
                          type="button" 
                          onClick={() => removeRoom(index)}
                          className="btn-remove-room"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      
                      <div className="form-group">
                        <label>Nome do Quarto</label>
                        <input
                          type="text"
                          value={room.name}
                          onChange={(e) => updateRoom(index, 'name', e.target.value)}
                          placeholder="Ex: Quarto Duplo Standard"
                          required
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Capacidade</label>
                          <input
                            type="number"
                            value={room.capacity}
                            onChange={(e) => updateRoom(index, 'capacity', e.target.value)}
                            placeholder="Pessoas"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Preço/Diária (R$)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={room.price}
                            onChange={(e) => updateRoom(index, 'price', e.target.value)}
                            placeholder="0.00"
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Descrição do Quarto</label>
                        <textarea
                          value={room.description}
                          onChange={(e) => updateRoom(index, 'description', e.target.value)}
                          rows="2"
                          placeholder="Comodidades, características, etc."
                        />
                      </div>
                    </div>
                  ))}

                  <div className="form-group">
                    <label>Preço Base da Pousada (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="Preço de referência"
                      required
                    />
                    <small>Valor de referência para exibição geral</small>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>{t('services.commission')} (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.commission}
                  onChange={(e) => setFormData({ ...formData, commission: e.target.value })}
                  required
                />
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-primary" disabled={uploading}>
                  {uploading ? 'Enviando imagem...' : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
